import { CheckOutlined, CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Flex, Form, Space, Steps, theme } from 'antd';
import React, { type ReactNode, useEffect, useRef, useState } from 'react';
import MealStep from './meal-step';
import {
  toEditableSiteMenuOrder,
  toWizardValues,
  type WizardItemValue,
  type WizardValues,
} from './site-menu-order-wizard.mapper';
import { BeveragesStep, ConfirmStep, ItemsStep } from './wizard-steps';
import { type ScheduledMealOptionsDto } from '../../../../../../dto/order-options/site-order-options-dto';
import { type CustomerSetting, type Site } from '../../../../../../entities';
import { useNotification } from '../../../../../../hooks/use-notification';
import utils from '../../../../../../utils/utils';
import { type EditableSiteMenuOrder } from '../../customer-order-form.utils';

const STEP_CONTENT_HEIGHT = 420;
const STEP_TITLES = [
  'Choose meal',
  'Choose beverages',
  'Choose extra beverages',
  'Choose supplies',
  'Confirm',
];
const STEP_ITEMS = STEP_TITLES.map((title) => ({ key: title, title }));
const LAST_STEP = STEP_TITLES.length - 1;

interface SiteMenuOrderWizardProps {
  /** The site menu order to edit, or undefined to add a new one */
  initialSiteMenuOrder?: EditableSiteMenuOrder;
  site: Site;
  customerSetting: CustomerSetting | undefined;
  availableMeals: ScheduledMealOptionsDto[];
  onConfirm: (siteMenuOrder: EditableSiteMenuOrder) => void;
  onCancel: () => void;
}

/** The steps to add a site menu order to a site's order, or to edit one */
const SiteMenuOrderWizard = ({
  initialSiteMenuOrder,
  site,
  customerSetting,
  availableMeals,
  onConfirm,
  onCancel,
}: SiteMenuOrderWizardProps): ReactNode => {
  const notification = useNotification();
  const { token } = theme.useToken();
  const [form] = Form.useForm<WizardValues>();
  const [initialValues] = useState(() => toWizardValues(initialSiteMenuOrder));
  const [temporaryId] = useState(() => initialSiteMenuOrder?.temporaryId ?? utils.randomUUID());
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedMeal, setSelectedMeal] = useState(() =>
    availableMeals.find((meal) => String(meal.id) === initialSiteMenuOrder?.scheduledMeal?.id)
  );

  // "preserve" keeps the values of the steps which aren't shown
  const beverages: Array<WizardItemValue | undefined> =
    Form.useWatch('beverages', { form, preserve: true }) ?? [];
  const quantity: number =
    Form.useWatch('quantity', { form, preserve: true }) ?? initialValues.quantity;
  const selectedBeveragesCount = beverages.reduce((sum, item) => sum + (item?.quantity ?? 0), 0);
  const allowedBeveragesCount = quantity * (selectedMeal?.mealCategory.beverageFactor ?? 1);
  const hasTooManyBeverages = selectedBeveragesCount > allowedBeveragesCount;

  // TODO: the prepack flag should be included only in the selected scheduled meal
  const showPrepackCheckbox = !!(
    customerSetting?.prepackMeals &&
    selectedMeal?.mealCategory.isPrepackable &&
    beverages.length > 0
  );

  // When the "prepack beverages" checkbox is hidden, prepackBeverages is false. When it shows, it is
  // checked by default, except for an edited site menu order, which keeps the choice it was saved
  // with. A site menu order saved without beverages never showed the checkbox, so its "false" isn't
  // a choice.
  const savedPrepackBeverages = useRef<boolean | undefined>(
    initialSiteMenuOrder && initialValues.beverages.length > 0
      ? initialValues.prepackBeverages
      : undefined
  );
  useEffect(() => {
    if (!showPrepackCheckbox) {
      form.setFieldValue('prepackBeverages', false);
      return;
    }
    form.setFieldValue('prepackBeverages', savedPrepackBeverages.current ?? true);
    // Only the first time it shows: unchecking and checking again afterwards is the user's choice
    savedPrepackBeverages.current = undefined;
  }, [showPrepackCheckbox]);

  // Another meal starts from its defaults, not from the previous meal's choices
  const handleMealChange = (mealId: string): void => {
    const meal = availableMeals.find((availableMeal) => String(availableMeal.id) === mealId);
    savedPrepackBeverages.current = undefined;
    setSelectedMeal(meal);
    form.setFieldsValue({
      beverages: [],
      extraBeverages: [],
      supplies: [],
      showDiets: false,
      showPreferences: false,
      mainType: 'Regular',
      serviceType: meal?.mealCategory.defaultServiceType,
    });
    form.setFieldValue('specialDiets', null);
    form.setFieldValue('preferences', null);
  };

  const goToNextStep = (): void => {
    form
      .validateFields()
      .then(() => {
        setCurrentStep((step) => step + 1);
      })
      .catch(() => {});
  };

  const confirm = (): void => {
    onConfirm(
      toEditableSiteMenuOrder(form.getFieldsValue(true), {
        id: initialSiteMenuOrder?.id,
        temporaryId,
        site,
        scheduledMeal: selectedMeal,
      })
    );
    notification.success({ message: ' ', description: 'Meal Added Successfully!' });
  };

  const renderStep = (): ReactNode => {
    switch (currentStep) {
      case 0:
        return (
          <MealStep
            site={site}
            availableMeals={availableMeals}
            selectedMeal={selectedMeal}
            onMealChange={handleMealChange}
          />
        );
      case 1:
        return (
          <BeveragesStep
            meal={selectedMeal}
            selectedCount={selectedBeveragesCount}
            allowedCount={allowedBeveragesCount}
            showPrepackCheckbox={showPrepackCheckbox}
          />
        );
      case 2:
        return (
          <ItemsStep
            name="extraBeverages"
            itemKey="beverage"
            items={selectedMeal?.extraBeverages ?? []}
            nothingToAddMessage={
              <>You can&apos;t add extra beverages for {selectedMeal?.mealCategory.name}</>
            }
            placeholder="Please select an extra beverages"
            missingItemMessage="Missing Extra Beverage"
            addLabel="Add Extra Beverage"
            showDescription
          />
        );
      case 3:
        return (
          <ItemsStep
            name="supplies"
            itemKey="supply"
            items={selectedMeal?.supplies ?? []}
            nothingToAddMessage="You can't add supplies for this meal category"
            placeholder="Please select supplies"
            missingItemMessage="Missing Supply"
            addLabel="Add supply"
          />
        );
      default:
        return <ConfirmStep meal={selectedMeal} />;
    }
  };

  const contentStyle: React.CSSProperties = {
    textAlign: 'center',
    color: token.colorTextTertiary,
    backgroundColor: token.colorFillAlter,
    borderRadius: token.borderRadiusLG,
    border: `1px dashed ${token.colorBorder}`,
    margin: 20,
    padding: 20,
    height: STEP_CONTENT_HEIGHT,
    overflow: 'auto',
    boxSizing: 'border-box',
  };

  return (
    <Space direction="vertical" size="large" style={{ padding: 20, width: '100%' }}>
      <Form
        name="dynamic_form_nest_item"
        autoComplete="off"
        form={form}
        colon={false}
        initialValues={initialValues}
      >
        <Steps current={currentStep} items={STEP_ITEMS} />
        <div style={contentStyle}>{renderStep()}</div>
        <Flex style={{ marginTop: 24 }} justify="center">
          <Button
            type="dashed"
            style={{ margin: '0 8px' }}
            icon={<CloseOutlined />}
            size="large"
            onClick={onCancel}
          >
            Discard
          </Button>
          <Button
            shape="circle"
            icon={<LeftOutlined />}
            disabled={currentStep === 0}
            size="large"
            style={{ margin: '0 8px' }}
            onClick={() => {
              setCurrentStep((step) => step - 1);
            }}
          />
          {currentStep < LAST_STEP ? (
            <Button
              type="primary"
              size="large"
              shape="circle"
              icon={<RightOutlined />}
              onClick={goToNextStep}
              // The beverages can't be more than the quantity allows, once it has been chosen
              disabled={currentStep > 0 && hasTooManyBeverages}
            />
          ) : (
            <Button
              type="primary"
              size="large"
              shape="circle"
              icon={<CheckOutlined />}
              onClick={confirm}
            />
          )}
        </Flex>
      </Form>
    </Space>
  );
};

export default SiteMenuOrderWizard;
